import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signIn, confirmSignIn, fetchAuthSession, fetchUserAttributes, rememberDevice } from 'aws-amplify/auth';
import { bootIntercom } from '../utils/intercom';
import { usePostHog } from 'posthog-js/react';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { useTheme } from '../context/ThemeContext';
import { applyUiPreferences, markShellEntrance, shellFromPreferences } from '../utils/uiPreferences';
import { consumeDeepLink } from '../utils/deepLink';
import hotelIcon from '../assets/img/Lodgeic-Logo.png';

const MagicLinkHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { fetchSettings } = useUserSettingsContext();
  const theme = useTheme();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'error' | 'mfa'
  const [error, setError] = useState('');

  const email = searchParams.get('email');
  const code = searchParams.get('code');

  useEffect(() => {
    if (!email || !code) {
      setStatus('error');
      setError('Invalid magic link. Please request a new one.');
      return;
    }

    handleMagicLink();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleMagicLink() {
    try {
      // Step 1: Initiate passwordless sign-in
      const signInResult = await signIn({
        username: email,
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'EMAIL_OTP',
        },
      });

      if (signInResult.nextStep.signInStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
        setStatus('error');
        setError('Unexpected response. Please try signing in manually.');
        return;
      }

      // Step 2: Auto-submit the code from the URL
      const confirmResult = await confirmSignIn({ challengeResponse: code });

      // Step 3: Handle result
      switch (confirmResult.nextStep.signInStep) {
        case 'DONE':
          await completeLogin();
          return;
        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION':
          // MFA still required — redirect to login page to handle it
          // The user is partially authenticated, login page will pick up the challenge
          navigate('/login?mfa=pending');
          return;
        default:
          setStatus('error');
          setError('Additional verification required. Please sign in manually.');
      }
    } catch (err) {
      console.error('Magic link error:', err);
      setStatus('error');
      setError('This link has expired or already been used. Please request a new one.');
    }
  }

  async function completeLogin() {
    try {
      await fetchAuthSession();
      const userAttributes = await fetchUserAttributes();

      try { await rememberDevice(); } catch {}

      bootIntercom({
        userId: userAttributes.email,
        name: userAttributes.given_name,
        email: userAttributes.email,
      });

      if (posthog && userAttributes.email) {
        posthog.setPersonPropertiesForFlags({
          email: userAttributes.email,
          name: userAttributes.given_name,
        });
        posthog.identify(userAttributes.email, {
          email: userAttributes.email,
          name: userAttributes.given_name,
        });
      }

      const loadedSettings = await fetchSettings();
      // Same as Login.enterApp: theme first so the shell's first frame is right.
      const prefs = loadedSettings?.uiPreferences;
      applyUiPreferences(theme, prefs);
      if ((shellFromPreferences(prefs) || theme.uiShell) === 'inset') markShellEntrance();
      navigate(consumeDeepLink('/dashboard'));
    } catch (err) {
      console.error('Post-login error:', err);
      setStatus('error');
      setError('Signed in but failed to load your data. Please try refreshing.');
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #FFF7ED 0%, #EFF6FF 50%, #DBEAFE 100%)' }}
    >
      <div className="text-center max-w-sm mx-auto px-6">
        <img src={hotelIcon} alt="Lodgeic" className="w-40 mx-auto mb-8" />

        {status === 'verifying' && (
          <div>
            <div className="w-8 h-8 border-3 border-orange-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-lg font-semibold text-slate-800">Signing you in...</p>
            <p className="text-sm text-slate-500 mt-2">Please wait while we verify your link.</p>
          </div>
        )}

        {status === 'error' && (
          <div>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-slate-800">Unable to sign in</p>
            <p className="text-sm text-slate-500 mt-2">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 px-6 py-2.5 rounded-xl font-semibold text-white text-sm"
              style={{ background: 'linear-gradient(to right, #F97316, #EA580C)' }}
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicLinkHandler;
